import {
	// Button,
	Col,
	Divider,
	Form,
	// Input,
	Row,
	Select,
	notification,
	theme,
} from 'antd';

import { Input, Button, Tooltip } from '@hydrodigital/design-system';

import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import type { ComboboxOption } from '@hydrodigital/combobox';

import React, { useEffect, useState } from 'react';
import UserService from '../../domain/Users/UserService';
import { msalInstance } from '../../utils/msal';
import { CheckCircleOutlined } from '@ant-design/icons';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
type LayoutType = Parameters<typeof Form>[0]['layout'];

const dataUTC = [
	{ label: '(GMT-12:00) Baker Island', value: -12 },
	{ label: '(GMT-11:00) Pago Pago (American Samoa), Midway', value: -11 },
	{ label: '(GMT-10:00) Honolulu, Papeete', value: -10 },
	{ label: '(GMT-09:00) Anchorage', value: -9 },
	{ label: '(GMT-08:00) Los Angeles, Vancouver, Tijuana', value: -8 },
	{ label: '(GMT-07:00) Denver, Phoenix, Calgary', value: -7 },
	{ label: '(GMT-06:00) Chicago, Mexico City, Guatemala City', value: -6 },
	{ label: '(GMT-05:00) New York, Lima, Bogotá', value: -5 },
	{ label: '(GMT-04:00) Caracas, Santiago, La Paz', value: -4 },
	{ label: '(GMT-03:00) Buenos Aires, São Paulo, Montevideo', value: -3 },
	{ label: '(GMT-02:00) South Georgia, Fernando de Noronha', value: -2 },
	{ label: '(GMT-01:00) Azores, Cape Verde', value: -1 },
	{ label: '(GMT+00:00) London, Lisbon, Accra', value: 0 },
	{ label: '(GMT+01:00) Paris, Berlin, Rome, Madrid', value: 1 },
	{ label: '(GMT+02:00) Cairo, Johannesburg, Athens, Bucharest', value: 2 },
	{ label: '(GMT+03:00) Moscow, Nairobi, Riyadh, Istanbul', value: 3 },
	{ label: '(GMT+04:00) Dubai, Baku, Tbilisi', value: 4 },
	{ label: '(GMT+05:00) Karachi, Tashkent, Yekaterinburg', value: 5 },
	{ label: '(GMT+06:00) Dhaka, Almaty, Omsk', value: 6 },
	{ label: '(GMT+07:00) Bangkok, Jakarta, Hanoi', value: 7 },
	{ label: '(GMT+08:00) Beijing, Singapore, Perth, Manila', value: 8 },
	{ label: '(GMT+09:00) Tokyo, Seoul, Osaka', value: 9 },
	{ label: '(GMT+10:00) Sydney, Port Moresby, Vladivostok', value: 10 },
	{ label: '(GMT+11:00) Nouméa, Honiara, Magadan', value: 11 },
	{ label: '(GMT+12:00) Auckland, Suva, Fiji', value: 12 },
	{ label: '(GMT+13:00) Nukuʻalofa, Apia', value: 13 },
	{ label: '(GMT+14:00) Kiritimati (Christmas Island)', value: 14 },
];

interface IProps {
	setAddModalUser: any;
	roles: any;
	modules: any;
	organizations: any;
	handleGetUsers: any;
	isNewUser: any;
	selectedUser: any;
	setSelectedUser: any;
	setSelectNav: any;
}

type PersonalInformationRefType = {
	resetForm: Function;
};

const userService = new UserService();

// eslint-disable-next-line react/display-name
const PersonalInformation = React.forwardRef<PersonalInformationRefType, IProps>(
	(
		{
			setAddModalUser,
			setSelectNav,
			roles,
			modules,
			organizations,
			isNewUser,
			selectedUser,
			setSelectedUser,
			handleGetUsers,
		}: IProps,
		ref,
	) => {
		const [form] = Form.useForm();
		const [formLayout, setFormLayout] = useState<LayoutType>('vertical');
		const currentSecurity = JSON.parse(localStorage.getItem('security') ?? '{}');
		const userRole = currentSecurity.role;
		const organizationId = currentSecurity.organizationId;

		const isEditingSuperAdmin = !isNewUser && selectedUser?.role === 'SuperAdmin';
		const isCurrentUserAdmin = userRole === 'Administrator';
		const isRoleLocked = isEditingSuperAdmin || (isCurrentUserAdmin && selectedUser?.role === 'SuperAdmin');
		const roleLockedReason = isEditingSuperAdmin
			? 'Super Admin role cannot be changed'
			: '';
		const filteredRoles = isCurrentUserAdmin
			? (roles || []).filter((r: any) => r.label !== 'SuperAdmin')
			: roles || [];

		const themes = [
			{ label: 'Light', value: 'LIGHT', type: 'mapbox://styles/mapbox/light-v11' },
			{ label: 'Dark', value: 'DARK', type: 'mapbox://styles/mapbox/dark-v11' },
			{ label: 'Streets', value: 'STREETS', type: 'mapbox://styles/mapbox/streets-v12' },
			{
				label: 'Satellite Streets',
				value: 'SATELLITE',
				type: 'mapbox://styles/mapbox/satellite-v9',
			},
			{ label: 'Outdoors', value: 'OUTDOORS', type: 'mapbox://styles/mapbox/outdoors-v12' },
		];

		React.useImperativeHandle(ref, () => ({
			resetForm() {
				form.resetFields();
			},
		}));

		const toNullIfEmpty = (v: unknown) => {
			const s = (v ?? '').toString().trim();
			return s === '' ? null : s;
		};

		const onFinish = async (values: any) => {
			if (!isNewUser && selectedUser) {
				const originalRoleId = String(selectedUser.roleId);
				const newRoleId = String(values.roleId);
				const roleChanged = originalRoleId !== newRoleId;

				if (roleChanged && selectedUser.role === 'SuperAdmin') {
					notification.warning({
						message: 'Action not allowed',
						description: 'Cannot change the role of a Super Admin user.',
					});
					return;
				}

				if (roleChanged && selectedUser.role === 'Administrator') {
					const adminRoleId = roles?.find((r: any) => r.label === 'Administrator')?.value;
					if (adminRoleId) {
						const oui = organizationId;
						const res = await userService.getAllUsers(oui, 1, 1, null, adminRoleId);
						if (res.totalRecords <= 1) {
							notification.warning({
								message: 'Action not allowed',
								description: 'Cannot change the role of the only administrator in the organization.',
							});
							return;
						}
					}
				}
			}

			const payload: any = {
				...values,
				createdBy: localStorage.getItem('userId') ?? '',
				modules: (values.modules ?? []).map((m: any) => ({
					id: m.value,
					name: m.label,
				})),
				phoneNumber: toNullIfEmpty(values.phoneNumber),
			};

			if (userRole === 'Administrator') {
				payload.organization = organizationId;
			}

			const resCreate: any = await userService.createUser(payload);

			const status =
				resCreate?.httpStatus ??
				resCreate?.status ??
				(typeof resCreate?.success === 'number' ? resCreate.success : undefined);

			const isSuccess =
				resCreate?.success === true || resCreate?.success === 1 || status === 200 || status === 201;

			if (!isSuccess) {
				const msg = resCreate?.message;
				const level: 'warning' | 'error' = status === 409 ? 'warning' : 'error';
				if (status === 409 && msg) {
					form.setFields([{ name: 'email', errors: [msg] }]);
				}
				return;
			}

			if (isNewUser) {
				notification.success({
					message: 'Successful',
					description: 'User created successfully',
				});
			} else {
				notification.success({
					message: 'Successful',
					description: 'User edited successfully',
				});
			}

			const isCurrentUser =
				!isNewUser &&
				selectedUser &&
				currentSecurity?.email === selectedUser?.email;

			if (isCurrentUser && String(selectedUser?.roleId) !== String(values.roleId)) {
				notification.info({
					message: 'Role changed',
					description: 'Your role has been updated. You will be logged out.',
				});
				setTimeout(() => {
					msalInstance.logoutRedirect({
						onRedirectNavigate: () => {
							localStorage.clear();
							return true;
						},
						postLogoutRedirectUri: '/',
					});
				}, 1500);
				return;
			}

			if (isCurrentUser) {
				const updatedSecurity = {
					...currentSecurity,
					firstname: values.firstName,
					lastname: values.lastName,
					timeZone: values.timeZone?.toString(),
					email: values.email,
					roleId: values.roleId,
					modules: (values.modules ?? []).map((m: any) => ({
						id: m.value,
						name: m.label,
					})),
					phoneNumber: toNullIfEmpty(values.phoneNumber) || '',
				};
				localStorage.setItem('security', JSON.stringify(updatedSecurity));

				if (values.themeId) {
					const selectedTheme = themes.find((theme) => theme.value === values.themeId);
					if (selectedTheme) {
						const currentOrgSettings = JSON.parse(
							localStorage.getItem('organizationSettings') ?? '{}',
						);
						const updatedOrgSettings = {
							...currentOrgSettings,
							mapSettings: {
								settings: {
									style: selectedTheme.type,
								},
							},
						};
						localStorage.setItem('organizationSettings', JSON.stringify(updatedOrgSettings));
					}
				}
			}

			if (!selectedUser) {
				const data =
					typeof resCreate.data === 'string' ? JSON.parse(resCreate.data) : resCreate.data;
				setSelectedUser({
					userId: data.userId,
					firstName: values.firstName,
					lastName: values.lastName,
					email: values.email,
					timeZone: values.timeZone,
					roleId: values.roleId,
					organization: values.organization,
					modules: values.modules,
					themeId: values.themeId,
				});
			}

			setSelectNav('AsignLocation');
		};

		const validatePassword = (rule: any, value: any, callback: any) => {
			callback();
			const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\S]{8,}$/;

			if (value && !passwordRegex.test(value)) {
				callback('The password does not fullfill the requirements');
			} else {
				callback();
			}
		};

		const onFinishFailed = (errorInfo: any) => {
			console.log('Failed:', errorInfo);
		};

		useEffect(() => {
			if (!isNewUser) {
				let foundTheme = themes[0];
				const themeValue = selectedUser?.settings?.[0]?.settings?.[0]?.value;
				if (themeValue) {
					const matchedTheme = themes.find((element) => element.type === themeValue);
					if (matchedTheme) {
						foundTheme = matchedTheme;
					}
				}

				form.setFieldsValue({
					userId: selectedUser?.userId,
					firstName: selectedUser?.firstName,
					lastName: selectedUser?.lastName,
					email: selectedUser?.email,
					timeZone: selectedUser?.timeZone ? String(Number.parseInt(selectedUser.timeZone)) : undefined,
					roleId: selectedUser?.roleId ? String(selectedUser.roleId) : undefined,
					themeId: foundTheme?.value,
					organization: selectedUser?.organizationId ? String(selectedUser.organizationId) : undefined,
					phoneNumber: selectedUser?.phoneNumber,
					modules: selectedUser?.modules.map((module: any) => {
						return {
							value: module.id,
							label: module.name,
						};
					}),
				});
			} else {
				form.setFieldsValue({});
			}
		}, [selectedUser, isNewUser]);

		return (
			<Form layout={formLayout} form={form} onFinish={onFinish} onFinishFailed={onFinishFailed}>
				<Form.Item hidden={true} name="userId">
					<Input size="small" />
				</Form.Item>
				<Row gutter={10}>
					<Col span={12}>
						<Form.Item
							label={<div style={{ width: 80 }}>First Name</div>}
							name="firstName"
							rules={[
								{
									required: true,
									message: 'Please input your first name!',
								},
							]}
						>
							<Input size="small" placeholder="First Name" />
						</Form.Item>
						<Form.Item
							label={<div style={{ width: 80 }}>Email</div>}
							name="email"
							rules={[
								{
									required: true,
									message: 'Please input your email!',
									type: 'email',
								},
							]}
						>
							<Input size="small" placeholder="Email" />
						</Form.Item>
						<Form.Item
							label={<div style={{ width: 80 }}>Role</div>}
							name="roleId"
							trigger="onValueChange"
							validateTrigger="onValueChange"
							rules={[
								{
									required: true,
									message: 'Please select your role!',
								},
							]}
						>
							{isRoleLocked ? (
								<Tooltip
									text={roleLockedReason}
									trigger="hover"
									position="up"
									maxWidth={250}
								>
									<Combobox
										items={(roles || []).map((r: any) => ({ label: r.label, value: String(r.value) }))}
										value={selectedUser?.roleId ? String(selectedUser.roleId) : undefined}
										onClear={() => {}}
										size="small"
										disabled
									>
										<ComboboxInput placeholder="Role" />
										<ComboboxContent>
											<ComboboxEmpty>No options found.</ComboboxEmpty>
											<ComboboxList>
												{(item: ComboboxOption) => (
													<ComboboxItem key={item.value} value={item}>
														{item.label}
													</ComboboxItem>
												)}
											</ComboboxList>
										</ComboboxContent>
									</Combobox>
								</Tooltip>
							) : (
								<Combobox
									items={filteredRoles.map((r: any) => ({ label: r.label, value: String(r.value) }))}
									onClear={() => form.setFieldValue('roleId', undefined)}
									size="small"
								>
									<ComboboxInput placeholder="Role" />
									<ComboboxContent>
										<ComboboxEmpty>No options found.</ComboboxEmpty>
										<ComboboxList>
											{(item: ComboboxOption) => (
												<ComboboxItem key={item.value} value={item}>
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							)}
						</Form.Item>
						<Form.Item
							label={<div style={{ width: 80 }}>Phone</div>}
							name="phoneNumber"
							rules={[
								{
									validator: (_, value) => {
										if (!value) return Promise.resolve();
										const cleaned = String(value).replace(/[^\d+]/g, '');
										if (!cleaned.startsWith('+')) {
											return Promise.reject('Please include country code (e.g. +977 9841234567)');
										}
										try {
											return isValidPhoneNumber(cleaned)
												? Promise.resolve()
												: Promise.reject(
														'Please enter a valid phone number (e.g. +977 9841234567)',
													);
										} catch {
											return Promise.reject(
												'Please enter a valid phone number (e.g. +977 9841234567)',
											);
										}
									},
								},
							]}
						>
							<Input
								size="small"
								placeholder="e.g. +977 9841234567"
								inputMode="tel"
								maxLength={25}
							/>
						</Form.Item>

						{userRole == 'SuperAdmin' ? (
							<Form.Item
								label={<div style={{ width: 80 }}>Organization</div>}
								name={'organization'}
								trigger="onValueChange"
								validateTrigger="onValueChange"
								rules={[
									{
										required: true,
										message: 'Please select your organization!',
									},
								]}
							>
								<Combobox
									items={organizations.map((o: any) => ({ label: o.label, value: String(o.value) }))}
									onClear={() => form.setFieldValue('organization', undefined)}
									size="small"
								>
									<ComboboxInput placeholder="Organization" />
									<ComboboxContent>
										<ComboboxEmpty>No options found.</ComboboxEmpty>
										<ComboboxList>
											{(item: ComboboxOption) => (
												<ComboboxItem key={item.value} value={item}>
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</Form.Item>
						) : (
							''
						)}
						{isNewUser ? (
							<div>
								<Form.Item
									label={<div style={{ width: 80 }}>Re-password</div>}
									name="rePassword"
									dependencies={['password']}
									rules={[
										{
											required: true,
											message: 'Please confirm your password!',
										},
										{
											min: 8,
											message: 'Password must be minimum 8 characters.',
										},
										{ validator: validatePassword },
										({ getFieldValue }) => ({
											validator(_, value) {
												if (!value || getFieldValue('password') === value) {
													return Promise.resolve();
												}
												return Promise.reject(
													new Error('The two passwords that you entered do not match!'),
												);
											},
										}),
									]}
									hasFeedback

									// Accepted Symbols @#$%^&*-_!+=[]|\:',.?/`~();"
								>
									<Input type="password" size="small" />
								</Form.Item>
							</div>
						) : (
							''
						)}
					</Col>
					<Col span={12}>
						<Form.Item
							label={<div style={{ width: 80 }}>Last Name</div>}
							name="lastName"
							rules={[
								{
									required: true,
									message: 'Please input your last name!',
								},
							]}
						>
							<Input size="small" placeholder="Last Name" />
						</Form.Item>
						<Form.Item
							label={<div style={{ width: 80 }}>Time Zone</div>}
							name="timeZone"
							trigger="onValueChange"
							validateTrigger="onValueChange"
							rules={[
								{
									required: true,
									message: 'Please select your Time Zone!',
								},
							]}
						>
							<Combobox
								items={dataUTC.map((d) => ({ label: d.label, value: String(d.value) }))}
								onClear={() => form.setFieldValue('timeZone', undefined)}
								size="small"
							>
								<ComboboxInput placeholder="Time Zone" />
								<ComboboxContent>
									<ComboboxEmpty>No options found.</ComboboxEmpty>
									<ComboboxList>
										{(item: ComboboxOption) => (
											<ComboboxItem key={item.value} value={item}>
												{item.label}
											</ComboboxItem>
										)}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
						</Form.Item>
						{/* empty modules */}
						<Form.Item
							label={<div style={{ width: 80 }}>Modules</div>}
							name="modules"
							rules={[
								{
									required: false,
									message: 'Please select your modules!',
								},
							]}
						>
							<Select
								labelInValue
								placeholder="Modules"
								mode="tags"
								options={modules}
							/>
						</Form.Item>
						{isNewUser && (
							<Form.Item
								label={<div style={{ width: 80 }}>Password</div>}
								name="password"
								rules={[
									{
										required: true,
										message: 'Please input your password!',
									},
									{ validator: validatePassword },
									{
										min: 8,
										message: 'Username must be minimum 8 characters.',
									},
								]}
								help={
									<div className="flex column">
										<div>
											<CheckCircleOutlined className="mr-1" />
											<span>Minimum 8 characters</span>
										</div>
										<div>
											<CheckCircleOutlined className="mr-1" />
											<span>Lowercase characters</span>
										</div>
										<div>
											<CheckCircleOutlined className="mr-1" />
											<span>Uppercase characters</span>
										</div>
									</div>
								}
								hasFeedback
							>
								<Input type="password" size="small" />
							</Form.Item>
						)}
					</Col>
				</Row>
				<Row>
					<Col span={12}>
						<Form.Item
							label={<div style={{ width: 80 }}>Theme</div>}
							name="themeId"
							trigger="onValueChange"
							validateTrigger="onValueChange"
							rules={[
								{
									required: true,
									message: 'Please select your theme!',
								},
							]}
						>
							<Combobox
								items={themes.map((t) => ({ label: t.label, value: t.value }))}
								onClear={() => form.setFieldValue('themeId', undefined)}
								size="small"
							>
								<ComboboxInput placeholder="Themes" />
								<ComboboxContent>
									<ComboboxEmpty>No options found.</ComboboxEmpty>
									<ComboboxList>
										{(item: ComboboxOption) => (
											<ComboboxItem key={item.value} value={item}>
												{item.label}
											</ComboboxItem>
										)}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
						</Form.Item>
					</Col>
				</Row>
				<Divider style={{ border: '1px solid #E6E6E6' }} />
				<div className="flex justify-between">
					<Button
						label="Cancel"
						showIcon={false}
						className="mr-3"
						style={{
							width: '85px',
							height: '40px',
							backgroundColor: '#F7F9FA',
							color: '#434B56',
							border: '1px solid #C4CCD5',
						}}
						onClick={() => setAddModalUser(false)}
					/>

					<Button
						label="Save and continue"
						showIcon={false}
						style={{
							width: '180px',
							height: '40px',
							backgroundColor: '#00203C',
							color: 'white',
						}}
						onClick={() => form.submit()}
					/>
				</div>
			</Form>
		);
	},
);

export default PersonalInformation;
